import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  InputAdornment,
  ClickAwayListener,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandMore } from '@material-ui/icons';
import { countries } from '../../utils/countries';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
  },
  phoneInput: {
    width: '100%',
  },
  countrySelector: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: '85px',
    backgroundColor: 'transparent',
    borderRadius: theme.spacing(0.5),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'scale(1.02)',
    },
  },
  flag: {
    fontSize: '1.3em',
    marginRight: theme.spacing(0.5),
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
  },
  phoneCode: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: '0.875rem',
    marginRight: theme.spacing(0.5),
    letterSpacing: '0.5px',
  },
  expandIcon: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease-in-out',
  },
  dropdown: {
    zIndex: 1300,
    maxHeight: 300,
    overflow: 'auto',
    minWidth: 280,
    boxShadow: theme.shadows[8],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(4px)',
    },
  },
  countryInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  countryName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  countryCode: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1,
  },
  phoneCodeInMenu: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '0.875rem',
    minWidth: '50px',
  }
}));

const CountryPhoneSelector = ({
  value = '',
  onChange,
  onCountryChange,
  label = 'WhatsApp',
  placeholder = 'Número de teléfono',
  variant = 'outlined',
  fullWidth = true,
  error = false,
  helperText = '',
  defaultCountry = 'BR',
  disabled = false,
  ...props
}) => {
  const classes = useStyles();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Inicializar país seleccionado
  useEffect(() => {
    const country = countries.find(c => c.code === defaultCountry) || countries[0];
    setSelectedCountry(country);
  }, [defaultCountry]);

  // Extraer número de teléfono del valor completo
  useEffect(() => {
    if (value && selectedCountry) {
      const phoneCode = selectedCountry.phoneCode;
      if (value.startsWith(phoneCode)) {
        setPhoneNumber(value.substring(phoneCode.length));
      } else {
        setPhoneNumber(value);
      }
    } else {
      setPhoneNumber(value || '');
    }
  }, [value, selectedCountry]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
    
    // Construir el número completo
    const fullNumber = country.phoneCode + phoneNumber;
    
    if (onChange) {
      onChange(fullNumber);
    }
    if (onCountryChange) {
      onCountryChange(country);
    }
  };

  const handlePhoneChange = (event) => {
    const newPhoneNumber = event.target.value;
    setPhoneNumber(newPhoneNumber);
    
    // Construir el número completo
    const fullNumber = selectedCountry ? selectedCountry.phoneCode + newPhoneNumber : newPhoneNumber;
    
    if (onChange) {
      onChange(fullNumber);
    }
  };

  const handleDropdownToggle = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setDropdownOpen(!dropdownOpen);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={handleDropdownClose}>
      <Box className={classes.root}>
        <TextField
          className={classes.phoneInput}
          variant={variant}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          value={phoneNumber}
          onChange={handlePhoneChange}
          error={error}
          helperText={helperText}
          disabled={disabled}
          type="tel"
          inputProps={{
            pattern: "[0-9]*",
            inputMode: "numeric"
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box 
                  className={classes.countrySelector}
                  onClick={handleDropdownToggle}
                >
                  <span className={classes.flag}>
                    {selectedCountry?.flag || '🌍'}
                  </span>
                  <span className={classes.phoneCode}>
                    {selectedCountry?.phoneCode || '+1'}
                  </span>
                  <ExpandMore 
                     className={classes.expandIcon} 
                     style={{ 
                       transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' 
                     }} 
                   />
                </Box>
              </InputAdornment>
            ),
          }}
          {...props}
        />

        <Popper
          open={dropdownOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          className={classes.dropdown}
        >
          <Paper>
            <List dense>
              {countries.map((country) => (
                <ListItem
                  key={country.code}
                  button
                  className={classes.menuItem}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className={classes.flag}>{country.flag}</span>
                  <span className={classes.phoneCodeInMenu}>{country.phoneCode}</span>
                  <Box className={classes.countryInfo}>
                    <Typography className={classes.countryName}>
                      {country.name}
                    </Typography>
                    <Typography className={classes.countryCode}>
                      {country.code}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default CountryPhoneSelector;